import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { SqlComposer } from 'src/app/utils/query/sql-composer';
import { BiDevMasterFunnelSqlMap } from '../sql-maps/bidev-master-funnel-sql-map';
import { QueryContextSettingID } from 'src/app/services/query-context/models/IQueryContext';

export interface FunnelsMasterFunnelDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {}

/**
 * Calculates stats related to master funnel
 * Emits data of schema:
 * - `master_funnel_step_id`
 * - `cnt`
 * - `master_funnel_step_no`
 * - `screen_name`
 * - `percentage`
 * - `pct_diff`
 * - `error`
 */
export class FunnelsMasterFunnelDataLoaderFactory extends Factory<
    DataLoader,
    FunnelsMasterFunnelDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'FunnelsMasterFunnelDataLoaderFactory';

    /** This function returns DataLoader instance for analytics vs store reports differences */
    getInstance(options: FunnelsMasterFunnelDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );
                console.log(
                    'FunnelsMasterFunnelDataLoaderFactory',
                    queryContext
                );

                // ignore columns that are not supported in this query
                // const ignoredContextColumnIds = [
                //     'FUN',
                // ];

                const bqTableSqlMap = BiDevMasterFunnelSqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    bqTableSqlMap.sqlMap.columns
                );

                const dsColumnsStatus = DataLoader.getMissingColumnsStatus(
                    dsColumns.missing
                );

                //filter out ignored columns from dsColumns.present
                dsColumns.present = dsColumns.present;
                // .filter(
                //     (col) => !ignoredContextColumnIds.includes(col.id)
                // );

                // let getDefaultMaxDayNo = (context: IQueryContext) => {
                //     return 'unlimited';
                // };

                // const maxDayNo = options.maxDayNoGetter
                //     ? options.maxDayNoGetter(queryContext)
                //     : getDefaultMaxDayNo(queryContext);

                // const maxDayNoSQL: Partial<ICompiledSQLTokens> | undefined =
                //     maxDayNo !== 'unlimited'
                //         ? {
                //               where: [`day_no <= ${maxDayNo}`],
                //           }
                //         : undefined;

                const {
                    splitSelect,
                    splitGroupBy,
                    where,
                    join,
                    windowRunning,
                } = bqTableSqlMap.compileSQLStatements(
                    dsColumns.present,
                    queryContext.settings,
                    options,
                    {
                        where: [
                            // 'step_dominance > 0.9'
                        ],
                    }
                    // maxDayNoSQL
                );

                const query = `
-- DataLoaderId: ${this.instanceId}
WITH aggregate AS
(
  SELECT
    *
    , cnt / SUM(cnt) OVER (PARTITION BY master_funnel_step_no) AS step_dominance
  FROM
  (
    SELECT
      master_funnel_step_id
      -- cannot rely on ANY_VALUE() becasue id does not ignore NULLs
      , MIN(master_funnel_step_no) AS master_funnel_step_no
      , MIN(screen_name) AS screen_name
      , COUNT(*) AS cnt
      , COUNT(DISTINCT player_id) AS cnt_distinct_player_id
    FROM \`${generateTableFullPath(
        options.gcpProjectId,
        bqTableSqlMap.sqlMap.dataset,
        bqTableSqlMap.sqlMap.tableId
    )}\`
    ${join}
    ${where}
    ${SqlComposer.GROUP_BY(['master_funnel_step_id'])}
  )
  -- filtr marginalnych lokacji
  -- nie chcemy takich stepów, które mają mniej niż 40 zdarzeń
  WHERE cnt > 40
  -- tylko te, które mają przynajmniej 10% dominacji
  QUALIFY step_dominance > 0.1
)

-- SELECT * FROM aggregate ORDER BY master_funnel_step_no, cnt DESC

-- funnel builder
-- dla każdego screen szukamy min i max
-- sortujemy:
--     co jeżeli jest taki sam min/max? 
--         sort by name? 
--         sort by first occurence?

-- dla każdego screen szukamy min i max
, screen_name_min_max_rn AS
(
  SELECT 
    screen_name
    , SUM(cnt) AS cnt
    , MIN(rn) AS first_rn
    , MAX(rn) AS last_rn
  FROM
  (
    SELECT 
      *
      , 4 + -- aby ułatwić porównanie z Excelem
        ROW_NUMBER() OVER (ORDER BY master_funnel_step_no, cnt DESC) 
      AS rn
    FROM aggregate
  )
  ${SqlComposer.GROUP_BY(['screen_name'])}
)

, rank_by_min_and_max AS
(
  SELECT 
    *
    , ROW_NUMBER() OVER (ORDER BY first_rn, cnt DESC) AS min_rn
    , ROW_NUMBER() OVER (ORDER BY last_rn, cnt DESC) AS max_rn
  FROM screen_name_min_max_rn
)

, screen_cnt AS
(
SELECT 
  ROW_NUMBER() OVER (ORDER BY min_rn) AS master_funnel_step_no
  , rank_by_min_and_max.screen_name
  , screen_cnt.cnt
FROM rank_by_min_and_max
LEFT JOIN
(
  SELECT
    screen_name
    , SUM(cnt) AS cnt
  FROM
  (
    SELECT *
    FROM
    (
      SELECT
        master_funnel_step_id
        -- cannot rely on ANY_VALUE() becasue id does not ignore NULLs
        , MIN(master_funnel_step_no) AS master_funnel_step_no
        , MIN(screen_name) AS screen_name
        , COUNT(*) AS cnt
        , COUNT(DISTINCT player_id) AS cnt_distinct_player_id
      FROM \`${generateTableFullPath(
          options.gcpProjectId,
          bqTableSqlMap.sqlMap.dataset,
          bqTableSqlMap.sqlMap.tableId
      )}\`
      ${join}
      ${where}
      ${SqlComposer.GROUP_BY(['master_funnel_step_id'])}
    )
    -- nie chcemy takich stepów, które mają mniej niż 40 zdarzeń
    WHERE cnt > 40
    QUALIFY cnt / SUM(cnt) OVER (PARTITION BY master_funnel_step_no) > 0.01
  )
  ${SqlComposer.GROUP_BY(['screen_name'])}
) screen_cnt
ON rank_by_min_and_max.screen_name = screen_cnt.screen_name
WHERE min_rn = max_rn
${SqlComposer.ORDER_BY(['min_rn'])}
)

SELECT
  CONCAT(master_funnel_step_no, ':', screen_name) AS master_funnel_step_id
  , cnt
  , 1 - cnt / LAG(cnt) OVER (ORDER BY master_funnel_step_no) AS pct_diff
  -- percent difference between first and current step
  , 1 - cnt / FIRST_VALUE(cnt) OVER (ORDER BY master_funnel_step_no) AS pct_diff_first
  , master_funnel_step_no
  , screen_name
FROM screen_cnt
${SqlComposer.ORDER_BY(['master_funnel_step_no'])}
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();
                var data = [...queryResponse.result];
                bq2echartsformat(data);

                this.emitter.emit({
                    data,
                    queryContext,
                    status: dsColumnsStatus.pendingDataLoaderResultStatus,
                    statusMessage: dsColumnsStatus.pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
